import { useAuth0 } from "ct-auth"
import { useNavigate, useRouteError } from "react-router-dom"
import logger from "../../Logger"
import { DEFAULT_LOGO, ROUTE_PATHS } from "../../utilities/constants"
import { isChunkLoadError } from "../../utilities/lazyWithRetry"
import "./error-page.scss"

const CHUNK_ERROR_TITLE = "We couldn't finish loading the app"
const CHUNK_ERROR_MESSAGE = "This usually means a new version was just released, or the connection dropped. Reloading will fix it."

const pageLogger = logger.child({ module: "Error" })
export default function ErrorPage({ title, message, showRequestNewInvitation = false }) {
  const error = useRouteError()
  const navigate = useNavigate()
  const { loginWithRedirect } = useAuth0()

  let errorDescription = null
  if (title || message) {
    pageLogger.info(`Error '${title}' occurred with message: ${message}`)
  } else {
    pageLogger.error({ error }, "Error!")
    errorDescription = error?.statusText ?? error?.message ?? "<No Error Available>"
  }

  // A code-split bundle that failed to download is not an application fault and the raw webpack
  // text ("Loading chunk 7488 failed…") means nothing to a user, so this one case gets its own
  // copy plus a Reload action. Only applies to the router errorElement path (no title/message
  // props); every other error keeps its existing wording and layout untouched.
  const chunkLoadFailure = !title && !message && isChunkLoadError(error)

  /**
   * Function to re-fetch the app after a failed chunk download
   */
  const handleReload = () => {
    window.location.reload()
  }

  /**
   * Function to Handle Logo Click
   */
  const handleLogoClick = (e) => {
    e.preventDefault()
    navigate(ROUTE_PATHS.GAME_ROOT)
  }

  /**
   * Function to kick off passwordless login (email code/link verify)
   */
  const handlePasswordlessLogin = async () => {
    const returnTo = ROUTE_PATHS.GAME_ROOT
    pageLogger.info("Logging in (passwordless)...")
    pageLogger.info(`Will return to: ${returnTo}...`)
    await loginWithRedirect({
      authorizationParams: {
        connection: "email",
      },
      appState: {
        returnTo,
      },
    })
  }

  return (
    <div id="error-page" className="p-3">
      <div className="d-flex align-items-center mb-2 mb-lg-0 text-dark text-decoration-none">
        <a href={ROUTE_PATHS.GAME_ROOT} onClick={handleLogoClick}>
          <img className="brand-logo" src={DEFAULT_LOGO} alt="brand logo" />
        </a>
      </div>
      <h1>{chunkLoadFailure ? CHUNK_ERROR_TITLE : title || "Oops!"}</h1>
      <p>{chunkLoadFailure ? CHUNK_ERROR_MESSAGE : message || "Sorry, an unexpected error has occurred."}</p>
      <p>
        {chunkLoadFailure ? (
          <small className="text-muted">
            <i>{errorDescription}</i>
          </small>
        ) : (
          <i>{errorDescription}</i>
        )}
      </p>
      {chunkLoadFailure ? (
        <button type="button" className="btn btn-primary mt-2" onClick={handleReload}>
          Reload
        </button>
      ) : (
        <></>
      )}
      {showRequestNewInvitation ? (
        <button className="btn btn-primary mt-2" onClick={handlePasswordlessLogin}>
          Request Access
        </button>
      ) : (
        <></>
      )}
    </div>
  )
}
